import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays,
  Inbox,
  LogOut,
  Search,
  Trash2,
  CheckCircle2,
  Clock,
  Mail,
  Phone,
  MessageSquare,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Logo } from "../../components/Logo";
import { useAuth, adminApi, formatApiError } from "../../lib/authClient";

export default function AdminInbox() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        adminApi.get("/bookings"),
        adminApi.get("/contact"),
      ]);
      setBookings(b.data);
      setContacts(c.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load data");
      if (err.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const doLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const markBooking = async (id, status) => {
    try {
      const { data } = await adminApi.patch(`/bookings/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const deleteBooking = async (id) => {
    try {
      await adminApi.delete(`/bookings/${id}`);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Booking deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const deleteContact = async (id) => {
    try {
      await adminApi.delete(`/contact/${id}`);
      setContacts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const filteredBookings = useMemo(() => {
    const q = query.toLowerCase();
    return bookings.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        b.email.toLowerCase().includes(q) ||
        b.service.toLowerCase().includes(q)
    );
  }, [bookings, query]);

  const filteredContacts = useMemo(() => {
    const q = query.toLowerCase();
    return contacts.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.message || "").toLowerCase().includes(q)
    );
  }, [contacts, query]);

  const pending = bookings.filter((b) => b.status === "pending").length;

  return (
    <div className="min-h-screen bg-[#000000]" data-testid="admin-inbox">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#000000]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[90rem] items-center justify-between px-6 py-4 md:px-10">
          <div className="flex items-center gap-4">
            <Logo className="h-7" />
            <span className="hidden font-mono text-xs uppercase tracking-[0.2em] text-white/65 sm:inline">
              / Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-white/75 md:inline">{user?.email}</span>
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white/70 hover:bg-white/5 transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-white hover:bg-white/5 transition-colors"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[90rem] px-6 py-10 md:px-10">
        <h1 className="font-display text-4xl font-extrabold tracking-tighter text-white md:text-5xl">
          Inbox
        </h1>
        <p className="mt-2 text-white/75">Manage all appointment bookings and contact enquiries.</p>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
          <StatCard icon={CalendarDays} label="Total bookings" value={bookings.length} />
          <StatCard icon={Clock} label="Pending" value={pending} accent />
          <StatCard icon={Inbox} label="Enquiries" value={contacts.length} />
        </div>

        {/* Controls */}
        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2 rounded-full border border-white/10 p-1">
            <TabButton active={tab === "bookings"} onClick={() => setTab("bookings")} testid="tab-bookings">
              Bookings ({bookings.length})
            </TabButton>
            <TabButton active={tab === "contacts"} onClick={() => setTab("contacts")} testid="tab-contacts">
              Enquiries ({contacts.length})
            </TabButton>
          </div>
          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/65" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              className="input-dark pl-10"
            />
          </div>
        </div>

        {/* Content */}
        <div className="mt-8">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="animate-spin text-white" size={28} />
            </div>
          ) : tab === "bookings" ? (
            <div className="grid gap-4" data-testid="bookings-list">
              {filteredBookings.length === 0 ? (
                <EmptyState label="No bookings yet." />
              ) : (
                <AnimatePresence>
                  {filteredBookings.map((b) => (
                    <BookingCard
                      key={b.id}
                      b={b}
                      onMark={markBooking}
                      onDelete={deleteBooking}
                    />
                  ))}
                </AnimatePresence>
              )}
            </div>
          ) : (
            <div className="grid gap-4" data-testid="contacts-list">
              {filteredContacts.length === 0 ? (
                <EmptyState label="No enquiries yet." />
              ) : (
                <AnimatePresence>
                  {filteredContacts.map((c) => (
                    <ContactCard key={c.id} c={c} onDelete={deleteContact} />
                  ))}
                </AnimatePresence>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-2xl border p-5 ${accent ? "border-white/40 bg-white/10" : "border-white/10 bg-[#0a0a0a]"}`}>
    <Icon size={20} className="text-white" />
    <div className="mt-3 font-display text-3xl font-extrabold tracking-tighter text-white">{value}</div>
    <div className="mt-1 font-mono text-xs uppercase tracking-[0.15em] text-white/65">{label}</div>
  </div>
);

const TabButton = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
      active ? "bg-white text-black" : "text-white/75 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const EmptyState = ({ label }) => (
  <div className="rounded-2xl border border-dashed border-white/10 py-20 text-center text-white/65">
    {label}
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    pending: "border-white/25 bg-white/[0.04] text-white/70",
    confirmed: "border-white/50 bg-white/10 text-white",
    completed: "border-white/35 bg-white/[0.06] text-white/85",
  };
  return (
    <span className={`rounded-full border px-3 py-1 font-mono text-[0.65rem] uppercase tracking-wider ${map[status] || map.pending}`}>
      {status}
    </span>
  );
};

const BookingCard = ({ b, onMark, onDelete }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.97 }}
    data-testid={`booking-card-${b.id}`}
    className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6"
  >
    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-display text-xl font-bold text-white">{b.name}</h3>
          <StatusPill status={b.status} />
        </div>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/75">
          <span className="flex items-center gap-2"><Mail size={14} className="text-white" />{b.email}</span>
          <span className="flex items-center gap-2"><Phone size={14} className="text-white" />{b.whatsapp}</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <span className="text-white">{b.service}</span>
          <span className="flex items-center gap-2 text-white/70"><CalendarDays size={14} className="text-white" />{b.date} · {b.time}</span>
          <span className="text-white/75">{b.meeting_type}</span>
        </div>
        {b.notes && <p className="mt-3 text-sm text-white/65">“{b.notes}”</p>}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {b.status !== "confirmed" && (
          <button
            onClick={() => onMark(b.id, "confirmed")}
            data-testid={`booking-confirm-${b.id}`}
            className="flex items-center gap-1.5 rounded-xl border border-white/25 bg-white/10 px-3 py-2 text-xs font-semibold text-white hover:bg-white/20 transition-colors"
          >
            <CheckCircle2 size={14} /> Confirm
          </button>
        )}
        {b.status !== "completed" && (
          <button
            onClick={() => onMark(b.id, "completed")}
            className="rounded-xl border border-white/15 bg-white/[0.04] px-3 py-2 text-xs font-semibold text-white/80 hover:bg-white/10 transition-colors"
          >
            Done
          </button>
        )}
        <button
          onClick={() => onDelete(b.id)}
          data-testid={`booking-delete-${b.id}`}
          className="flex items-center justify-center rounded-xl border border-white/15 bg-white/[0.02] p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
          aria-label="Delete booking"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  </motion.div>
);

const ContactCard = ({ c, onDelete }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.97 }}
    data-testid={`contact-card-${c.id}`}
    className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6"
  >
    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div className="min-w-0">
        <h3 className="font-display text-xl font-bold text-white">{c.name}</h3>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/75">
          <span className="flex items-center gap-2"><Mail size={14} className="text-white" />{c.email}</span>
          {c.phone && <span className="flex items-center gap-2"><Phone size={14} className="text-white" />{c.phone}</span>}
          {c.service && <span className="text-white">{c.service}</span>}
        </div>
        <p className="mt-3 flex items-start gap-2 text-sm text-white/70">
          <MessageSquare size={15} className="mt-0.5 shrink-0 text-white" />
          {c.message}
        </p>
      </div>
      <button
        onClick={() => onDelete(c.id)}
        data-testid={`contact-delete-${c.id}`}
        className="flex shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/[0.02] p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors"
        aria-label="Delete enquiry"
      >
        <Trash2 size={15} />
      </button>
    </div>
  </motion.div>
);
